import { Injectable } from '@angular/core';
import { ResponseType } from '@microsoft/microsoft-graph-client';

import { GraphAuthService } from '../auth/services/graph-auth.service';

/**
 * Best-effort Microsoft Graph profile photos for users identified by their principal name.
 * Uses the existing Graph authentication/client configuration; it does not call signIn().
 * Missing photos and request failures are optional enrichment failures, not UI errors.
 * Callers own fallback avatars and any object URLs created from the returned blobs.
 */
@Injectable({ providedIn: 'root' })
export class GraphProfileService {
    /**
     * Shares in-flight requests and caches both photos and unavailable results by normalized UPN.
     * Entries live for this service instance; there is no expiry or account-change invalidation.
     */
    private readonly photoCache = new Map<string, Promise<Blob | undefined>>();

    constructor(private graphAuth: GraphAuthService) {}

    /**
     * Retrieves a 48x48 profile photo when Graph has an authenticated account.
     * Authentication is checked on every call, including calls for cached photos.
     *
     * @param userPrincipalName Sender's Microsoft Entra user principal name (usually an email
     * address). Trimmed and lowercased for lookup; external addresses may not resolve in Graph.
     * @returns The photo blob, or undefined for an empty name, no authenticated account,
     * missing client/photo, insufficient permissions, or any authentication/request failure.
     * Unauthenticated calls are not cached. Once a photo request starts, its unavailable result
     * is cached too, so subsequent authenticated calls do not automatically retry failures.
     */
    public async getUserPhoto(userPrincipalName: string): Promise<Blob | undefined> {
        const normalizedUserPrincipalName = userPrincipalName.trim().toLowerCase();
        if (!normalizedUserPrincipalName) return undefined;

        try {
            if (!(await this.graphAuth.isAuthenticated())) return undefined;

            let photo = this.photoCache.get(normalizedUserPrincipalName);
            if (!photo) {
                photo = this.loadUserPhoto(normalizedUserPrincipalName).catch(() => undefined);
                this.photoCache.set(normalizedUserPrincipalName, photo);
            }

            return await photo;
        } catch {
            return undefined;
        }
    }

    /**
     * Requests the fixed-size binary photo using the configured Graph client and its permissions.
     * Returns undefined if no client is available; request errors are handled by getUserPhoto().
     */
    private async loadUserPhoto(userPrincipalName: string): Promise<Blob | undefined> {
        const client = await this.graphAuth.getGraphClient();
        if (!client) return undefined;

        return client.api(`/users/${encodeURIComponent(userPrincipalName)}/photos/48x48/$value`).responseType(ResponseType.BLOB).get();
    }
}
